import type { FC } from "react";

import classNames from "classnames";

import { TierBadge } from "../TierBadge";
import { TimeLimitedBadge } from "../TimeLimitedBadge";

import styles from "./VirtualPrizeBadges.module.scss";

export type VirtualPrizeBadgesProps = {
  isMoreThanOneDay?: boolean;
  color: "white" | "black";
  tierRestrictedLabel?: string;
  isTierRestricted: boolean;
  tier?: string;
  outOfStockLabel: string;
  isOutOfStock: boolean;
  hasLimitedOfferBadge: boolean;
  newBadgeLabel: string;
  showNewBadge: boolean;
  timeLimitedBadgeLabel?: string;
  isPlacedInSmallBox?: boolean;
  shouldResetPositioning?: boolean;
};

export const VirtualPrizeBadges: FC<VirtualPrizeBadgesProps> = ({
  color,
  hasLimitedOfferBadge,
  newBadgeLabel,
  showNewBadge,
  isTierRestricted,
  tier,
  tierRestrictedLabel,
  outOfStockLabel,
  isOutOfStock,
  timeLimitedBadgeLabel,
  isMoreThanOneDay,
  isPlacedInSmallBox,
  shouldResetPositioning,
}) => {
  const theme = color === "white" ? "dark-theme" : "light-theme";

  const getElementClasses = ({
    isOutOfStock,
    isLabel,
    isLeftPlacement,
    isWrapper,
    isNewBadge,
  }: {
    isOutOfStock?: boolean;
    isLabel?: boolean;
    isLeftPlacement?: boolean;
    isWrapper?: boolean;
    isNewBadge?: boolean;
  }) => {
    const isLeft = isLeftPlacement === true;
    const isRight = isLeftPlacement === false;
    return classNames({
      [styles.right]: isRight && !shouldResetPositioning,
      [styles.rightSmallBox]: isRight && isPlacedInSmallBox,
      [styles.left]: isLeft && !shouldResetPositioning,
      [styles.leftSmallBox]: isLeft && isPlacedInSmallBox,
      [styles.label]: isLabel,
      [styles.wrapper]: isWrapper,
      [styles.outOfStock]: isOutOfStock,
      [styles.newBadge]: isNewBadge,
    });
  };

  if (isOutOfStock) {
    return (
      <div className={getElementClasses({ isOutOfStock: true, isLabel: true, isLeftPlacement: false })}>
        {outOfStockLabel}
      </div>
    );
  }

  if (isTierRestricted) {
    return (
      <TierBadge className={getElementClasses({ isLeftPlacement: false })} tier={tier} text={tierRestrictedLabel} />
    );
  }

  return (
    <>
      {!hasLimitedOfferBadge && showNewBadge && (
        <span
          data-testid={"newPrizeBadge"}
          className={getElementClasses({ isLabel: true, isLeftPlacement: true, isNewBadge: true })}
        >
          {newBadgeLabel}
        </span>
      )}
      <TimeLimitedBadge
        isMoreThanOneDay={isMoreThanOneDay}
        isDisabled={!hasLimitedOfferBadge}
        theme={theme}
        label={timeLimitedBadgeLabel}
        className={getElementClasses({ isLeftPlacement: true })}
      />
      <TierBadge
        className={getElementClasses({ isLeftPlacement: false })}
        isAvailable={true}
        tier={tier}
        text={tierRestrictedLabel}
      />
    </>
  );
};
